// The two "do this before anything else" flags the login response can set,
// kept in localStorage so a refresh can't escape them (ProtectedRoute reads
// them and bounces). Both are cleared by the screen that satisfies them and
// by logoutProcessor; the BE re-sends them on the next login anyway, so a
// stale flag is at worst one extra redirect.
//
//   accountSetup          — one-time migration: username (+ email for
//                           Manager+) + policy password → /account-setup
//   requirePasswordChange — forced rotation → /change-password; the BE's
//                           reason ("forced" | "expired", 2026-10-20) is kept
//                           alongside so the screen can say why

import type { AccountSetupFlags } from "../../types/Auth";

const SETUP_KEY = "accountSetup";
const ROTATE_KEY = "requirePasswordChange";
const ROTATE_REASON_KEY = "requirePasswordChangeReason";

/** Why the BE set requirePasswordChange: an admin flag / policy rollout, or a
 *  password older than the 90-day limit (helpers/passwordPolicy PASSWORD_MAX_AGE_DAYS). */
export type PasswordChangeReason = "forced" | "expired";
const isReason = (v: unknown): v is PasswordChangeReason => v === "forced" || v === "expired";

export interface StoredAccountSetup extends AccountSetupFlags {
    /** The handle already on the account (null when `username` is true),
     *  so the setup screen can show it read-only and pair the saved
     *  credential correctly. */
    currentUsername: string | null;
}

export const anySetupOutstanding = (flags: AccountSetupFlags | null | undefined): boolean =>
    !!flags && (flags.username === true || flags.email === true || flags.password === true);

export const readAccountSetup = (): StoredAccountSetup | null => {
    try {
        const raw = window.localStorage.getItem(SETUP_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        const flags: StoredAccountSetup = {
            username: parsed.username === true,
            email: parsed.email === true,
            password: parsed.password === true,
            currentUsername: typeof parsed.currentUsername === "string" ? parsed.currentUsername : null,
        };
        return anySetupOutstanding(flags) ? flags : null;
    } catch {
        return null;
    }
};

export const setAccountSetup = (flags: StoredAccountSetup): void => {
    try { window.localStorage.setItem(SETUP_KEY, JSON.stringify(flags)); } catch { /* storage unavailable */ }
};

export const clearAccountSetup = (): void => {
    try { window.localStorage.removeItem(SETUP_KEY); } catch { /* storage unavailable */ }
};

export const readRequirePasswordChange = (): boolean => {
    try { return window.localStorage.getItem(ROTATE_KEY) === "true"; } catch { return false; }
};

export const setRequirePasswordChange = (reason: PasswordChangeReason | null = null): void => {
    try {
        window.localStorage.setItem(ROTATE_KEY, "true");
        if (isReason(reason)) window.localStorage.setItem(ROTATE_REASON_KEY, reason);
        else window.localStorage.removeItem(ROTATE_REASON_KEY);
    } catch { /* storage unavailable */ }
};

/** null when no reason was stored (older BE build, or nothing pending). */
export const readRequirePasswordChangeReason = (): PasswordChangeReason | null => {
    try {
        const v = window.localStorage.getItem(ROTATE_REASON_KEY);
        return isReason(v) ? v : null;
    } catch { return null; }
};

export const clearRequirePasswordChange = (): void => {
    try {
        window.localStorage.removeItem(ROTATE_KEY);
        window.localStorage.removeItem(ROTATE_REASON_KEY);
    } catch { /* storage unavailable */ }
};

export const clearAuthFlags = (): void => {
    clearAccountSetup();
    clearRequirePasswordChange();
};
